import { useMemo } from 'react'

import { Alert } from '@vtex/shoreline'

import Panel from '~/components/Panel'
import { Badge } from '~/components/UI/Badge'
import { InputHidden, Label } from '~/components/unform'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

/**
 * Componente de orçamento dinâmico para campanhas de Retail Media.
 * Exibe informações sobre duração, locais, custo diário e custo total.
 * Não permite edição direta dos valores, apenas exibe os dados de acordo com a configuração da campanha.
 *
 * @param {string} Title - Título do painel.
 * @param {Object} values - Valores do orçamento dinâmico.
 * @param {boolean} isForm - Indica se o componente está em um formulário.
 * @param {boolean} isTemporaryValidationID - Indica se a campanha está em validação temporária.
 *
 * @returns {JSX.Element} Componente de orçamento dinâmico.
 */

const Dynamic: BudgetComponent = ({
  Title,
  values,
  isForm = false,
  isTemporaryValidationID = false
}) => {
  const { t } = useAPPTranslation()
  const hasTotalCost = useMemo(
    () => !!values.totalCostValue && values.totalCostValue !== 0,
    [values.totalCostValue]
  )

  const hasPending = useMemo(() => {
    const dynamicValues = {
      minDailyBudgetValue: values?.minDailyBudgetValue,
      duration: values?.duration,
      totalPlaces: values?.totalPlaces,
      totalDailyCost: values?.totalDailyCost,
      totalCostValue: values?.totalCostValue,
      totalCost: values?.totalCost
    }
    return Object.values(dynamicValues).some(value => !value)
  }, [
    values?.minDailyBudgetValue,
    values?.duration,
    values?.totalPlaces,
    values?.totalDailyCost,
    values?.totalCostValue,
    values?.totalCost
  ])

  const totalCostContent = useMemo(() => {
    if (hasTotalCost) {
      return values?.totalCost
    }
    return <Badge template="warning">{t('rm:pending')}</Badge>
  }, [hasTotalCost, values.totalCost, t])

  return (
    <Panel
      title={Title}
      description={t('rm:campaign.create.Investment.description')}
      data-investment-budget="dynamic"
    >
      {isForm && hasPending && (
        <header className="mb-3">
          <Alert variant="warning">
            {t('rm:campaign.create.Investment.info.pendingValuesWillFilled')}
          </Alert>
        </header>
      )}

      <section className="d-flex gap-3">
        <div className="pe-3 border-end">
          <Label text={t('rm:campaign.create.Investment.duration.title')} />
          <span>
            {values.duration ? (
              t('rm:campaign.create.Investment.duration', {
                duration: values.duration
              })
            ) : (
              <Badge template="warning">
                {t('rm:campaign.create.Investment.duration.placeholder')}
              </Badge>
            )}
          </span>
        </div>

        <div className="span-start pe-3 border-end">
          <Label text={t('rm:campaign.create.Investment.places.title')} />
          <span>
            {values.totalPlaces || (
              <Badge template="warning">
                {t('rm:campaign.create.Investment.places.placeholder')}
              </Badge>
            )}
          </span>
        </div>

        {!isTemporaryValidationID && (
          <div className="pe-3 border-end">
            <Label text={t('rm:campaign.create.Investment.dailyCost.title')} />
            <span>
              {values.totalDailyCost || (
                <Badge template="warning">{t('rm:pending')}</Badge>
              )}
            </span>
          </div>
        )}

        <div>
          <Label text={t('rm:campaign.create.Investment.totalCost.title')} />
          <span>{totalCostContent}</span>
        </div>

        {isForm && (
          <InputHidden name="total_budget" value={values.totalCostValue} />
        )}
      </section>

      <footer className="mt-3">
        <Alert variant="informational">
          {t('rm:campaign.create.Investment.info.dailyCost')}
        </Alert>
      </footer>
    </Panel>
  )
}

export default Dynamic
